/**
 * Order and payment math. Pure functions only: the Worker, the SPA and the unit
 * tests all compute totals through this module, so a DP order can never show one
 * balance on screen and another in the database.
 */

import { ORDER_STATUSES, SETTLEMENT_TYPES, type OrderStatus, type SettlementType } from "../constants/orders";
import { moneyEquals, percentOf, roundMoney, sumMoney } from "./money";

export interface TotalsLineInput {
  unit_price: number;
  quantity: number;
}

export interface OrderTotals {
  subtotal: number;
  tax_amount: number;
  total_amount: number;
}

export const computeLineTotal = (line: TotalsLineInput): number =>
  roundMoney(line.unit_price * line.quantity);

export const computeOrderTotals = (
  lines: readonly TotalsLineInput[],
  taxPercent = 0,
): OrderTotals => {
  const subtotal = sumMoney(lines.map(computeLineTotal));
  const taxAmount = percentOf(subtotal, taxPercent);
  return {
    subtotal,
    tax_amount: taxAmount,
    total_amount: roundMoney(subtotal + taxAmount),
  };
};

export interface PaymentState {
  total_amount: number;
  paid_amount: number;
  balance_amount: number;
  status: OrderStatus;
  is_settled: boolean;
  is_overpaid: boolean;
}

export const computePaymentState = (totalAmount: number, paidAmount: number): PaymentState => {
  const total = roundMoney(totalAmount);
  const paid = roundMoney(paidAmount);
  const balance = roundMoney(total - paid);

  if (moneyEquals(paid, 0)) {
    return {
      total_amount: total,
      paid_amount: paid,
      balance_amount: balance,
      status: ORDER_STATUSES.unpaid,
      is_settled: false,
      is_overpaid: false,
    };
  }

  if (balance > 0) {
    return {
      total_amount: total,
      paid_amount: paid,
      balance_amount: balance,
      status: ORDER_STATUSES.partiallyPaid,
      is_settled: false,
      is_overpaid: false,
    };
  }

  return {
    total_amount: total,
    paid_amount: paid,
    balance_amount: balance,
    status: ORDER_STATUSES.paid,
    is_settled: balance === 0,
    is_overpaid: balance < 0,
  };
};

export interface SettlementPlan extends PaymentState {
  settlement_type: SettlementType;
  initial_payment: number;
}

/**
 * Full settlement requires the entire total up front; a DP order must leave a
 * positive balance. Throws when the requested amount cannot form a valid order.
 */
export const computeSettlementPlan = (params: {
  totalAmount: number;
  settlementType: SettlementType;
  paidAmount: number;
}): SettlementPlan => {
  const { totalAmount, settlementType, paidAmount } = params;
  const total = roundMoney(totalAmount);
  const paid = roundMoney(paidAmount);

  if (settlementType === SETTLEMENT_TYPES.full && !moneyEquals(paid, total)) {
    throw new RangeError("Full settlement must be paid in full.");
  }
  if (total <= 0) {
    throw new RangeError("Order total must be greater than zero.");
  }
  if (paid < 0 || paid > total) {
    throw new RangeError("Payment amount must be between zero and the order total.");
  }
  if (settlementType === SETTLEMENT_TYPES.downPayment && (paid <= 0 || moneyEquals(paid, total))) {
    throw new RangeError("Down payment must be greater than zero and less than the total.");
  }

  return {
    ...computePaymentState(total, paid),
    settlement_type: settlementType,
    initial_payment: paid,
  };
};

export type PaymentValidation =
  | { ok: true; amount: number; next_paid: number; next_balance: number; next_status: OrderStatus }
  | { ok: false; reason: "not_positive" | "exceeds_balance" | "not_payable" };

export const validateSettlementPayment = (params: {
  totalAmount: number;
  paidAmount: number;
  amount: number;
  orderStatus: OrderStatus;
}): PaymentValidation => {
  const { totalAmount, paidAmount, amount, orderStatus } = params;

  if (orderStatus === ORDER_STATUSES.cancelled || orderStatus === ORDER_STATUSES.draft) {
    return { ok: false, reason: "not_payable" };
  }

  const rounded = roundMoney(amount);
  if (rounded <= 0) return { ok: false, reason: "not_positive" };

  const balance = roundMoney(totalAmount - paidAmount);
  if (rounded > balance) return { ok: false, reason: "exceeds_balance" };

  const nextPaid = roundMoney(paidAmount + rounded);
  return {
    ok: true,
    amount: rounded,
    next_paid: nextPaid,
    next_balance: roundMoney(totalAmount - nextPaid),
    next_status: computePaymentState(totalAmount, nextPaid).status,
  };
};

/** Which invoice kind a settlement produces. */
export const settlementInvoiceKind = (
  settlementType: SettlementType,
  isFirstPayment: boolean,
): "full" | "down_payment" | "balance" => {
  if (isFirstPayment && settlementType === SETTLEMENT_TYPES.downPayment) return "down_payment";
  if (isFirstPayment) return "full";
  return "balance";
};
